/**
 * TasksAwaitingReview — TASK-685.
 *
 * The one surface in Kielis that answers "what did you get done while I was away, and what
 * needs me?". Everything else shows work still to be started; this shows work already
 * finished and parked for sign-off (the `review` status from TASK-684).
 *
 * Book-level per `DASHBOARD-WIDGETIZATION.md`: it takes no `clientId` — "what needs my
 * sign-off" must never demand a client. The account rides the `X-Account-ID` header that
 * `lib/httpClient` injects, so there is no id prop at all and the model never emits a UUID.
 *
 * Grounding rule: it renders `GET /api/tasks?status=review` and nothing else. No mock rows,
 * and an empty result says so rather than implying work exists. A failed request renders as
 * a failure — never as "nothing waiting", which would be the most expensive possible lie on
 * this particular widget.
 */

import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { taskService } from '../../../services/taskService';
import { useCompleteTask } from '../../../hooks/useTasksQuery';
import { getPriorityClass } from '../../../types/tasks';
import type { Task, TaskArtifact, TaskDetailResponse } from '../../../types/tasks';
import { TaskAgentOutput } from '../../tasks/TaskAgentOutput';
import TaskOutcomePicker, { type TaskOutcomeSubmission } from '../../tasks/TaskOutcomePicker';
import { gatesSignOff, requiresAcknowledgement } from '../../tasks/taskArtifactResolver';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetErrorState } from '../../ui/WidgetErrorState';
import { toast } from 'sonner';

/** Mirrors `task_routes.MIN_EXCEPTION_REASON_CHARS`; the server re-validates and 400s. */
const MIN_EXCEPTION_REASON = 20;

export interface TasksAwaitingReviewProps {
  /** Optional short label; the widget renders its own title otherwise. */
  title?: string;
}

/**
 * TASK-702: the expanded row. Detail is fetched only when the RM opens a row — `artifacts`
 * and `events` deliberately stay off the list-row `Task` type.
 */
function ReviewRowDetail({
  taskId,
  onOpened,
  onAcknowledged,
  onUnreadable,
  acknowledged,
  onLoaded,
}: {
  taskId: string;
  onOpened: (artifact: TaskArtifact) => void;
  onAcknowledged: (artifact: TaskArtifact) => void;
  /** The RM accepts that this task's output could not be loaded at all. */
  onUnreadable: (taskId: string) => void;
  acknowledged: ReadonlySet<string>;
  /** Reports the loaded detail up so the sign-off gate can react to it. */
  onLoaded: (taskId: string, detail: TaskDetailResponse) => void;
}) {
  const detail = useQuery({
    queryKey: ['task-detail', taskId],
    queryFn: () => taskService.getTaskDetail(taskId),
  });

  // The gate lives on the parent, so the parent has to KNOW. Peeking at the query cache
  // instead looks tidier and is wrong: the parent has no subscription to that key, so the
  // button stays enabled until some unrelated state change happens to re-render it.
  const loaded = detail.data;
  useEffect(() => {
    if (loaded) onLoaded(taskId, loaded);
  }, [loaded, taskId, onLoaded]);

  if (detail.isLoading) {
    return <p className="text-xs text-muted-foreground">Loading what the agent produced…</p>;
  }
  if (detail.isError || !detail.data) {
    // Never "nothing to show": that reads as "the agent produced nothing", which is the
    // single most expensive lie on a sign-off surface.
    //
    // Sign-off stays blocked while this is on screen. The escape hatch is the same one an
    // unopenable artifact gets — otherwise a task whose detail endpoint is broken would be
    // permanently unsignable, which is the trap TASK-702 recorded.
    return (
      <div data-testid="review-detail-error" className="text-xs text-amber-800">
        <p>Could not load what the agent produced. Do not sign off on this until you can read it.</p>
        {acknowledged.has(taskId) ? (
          <p className="mt-1" data-testid="review-detail-acknowledged">
            You have accepted that this cannot be read.
          </p>
        ) : (
          <button
            type="button"
            onClick={() => onUnreadable(taskId)}
            data-testid="review-detail-acknowledge"
            className="mt-1 rounded border border-amber-300 px-1.5 py-0.5 text-[11px] font-medium hover:bg-amber-100"
          >
            I accept that this cannot be read
          </button>
        )}
      </div>
    );
  }
  return (
    <TaskAgentOutput
      detail={detail.data}
      onArtifactOpened={onOpened}
      onArtifactAcknowledged={onAcknowledged}
      acknowledged={acknowledged}
    />
  );
}

export function TasksAwaitingReview({ title }: TasksAwaitingReviewProps) {
  const navigate = useNavigate();
  const completeTask = useCompleteTask();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Which tasks have had their (resolvable) artifact opened IN THIS REVIEW. Deliberately not
  // persisted: the gate is "you read it before signing", not "someone read it once".
  const [readArtifacts, setReadArtifacts] = useState<Set<string>>(new Set());
  // Artifacts the RM has accepted they cannot open. Same lifetime as `readArtifacts` and for
  // the same reason — this is a statement about THIS review, not a stored fact.
  const [ackedArtifacts, setAckedArtifacts] = useState<Set<string>>(new Set());
  // Detail for the rows the RM has opened. Held here because the sign-off gate depends on it.
  const [details, setDetails] = useState<Record<string, TaskDetailResponse>>({});
  // TASK-758: which task's exception form is open, and the reason typed into it. Deliberately
  // NOT persisted — an exception is a decision made now, about this task, by this person.
  const [exceptionFor, setExceptionFor] = useState<string | null>(null);
  const [exceptionReason, setExceptionReason] = useState('');
  // TASK-922: which task is being asked what happened, and the exception reason (if any) it
  // was already carrying. Signing off work raised from a signal needs BOTH — the server
  // validates the exception reason first and then rejects the missing outcome, so an
  // exception sign-off on a subject-bound task 400s just as a plain one does.
  const [outcomeFor, setOutcomeFor] =
    useState<{ taskId: string; exceptionReason?: string } | null>(null);

  const rememberDetail = useCallback((taskId: string, detail: TaskDetailResponse) => {
    setDetails((prev) => (prev[taskId] === detail ? prev : { ...prev, [taskId]: detail }));
  }, []);

  const query = useQuery({
    queryKey: ['tasks', 'awaiting-review'],
    queryFn: () => taskService.fetchTasks({ status: 'review', limit: 50, sort_by: 'updated_at' }),
  });

  const tasks: Task[] = query.data?.tasks ?? [];

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  /**
   * Whether sign-off is allowed yet.
   *
   * The row must be OPENED first. A collapsed row shows a title, a priority pill, "prepared 2
   * hours ago" and a ✓ — which is the rubber stamp on unread work this widget exists to
   * remove. Gating only after the RM expands the row left the default state of every row
   * signable without the output ever being fetched, let alone read.
   *
   * Then every declared artifact must be dealt with — but "dealt with" differs by whether the
   * work can be read at all:
   *
   *  - it resolves  → the RM must OPEN it. Signing off on unread work is the defect.
   *  - it does not  → the RM must ACKNOWLEDGE that it cannot be opened. Both live artifacts on
   *    the demo account are in this state (one id is not a uuid; the other points at no draft
   *    row), and a hard block would leave those tasks permanently unsignable — the reviewer
   *    stuck forever behind an id they cannot fix. So the hatch exists; it just is not free.
   */
  const gate = (task: Task): { allowed: boolean; reason?: string; exceptional?: boolean } => {
    const detail = details[task.id];
    if (!detail) {
      // TASK-758: the detail endpoint failed, or the row was never opened. Either way the work
      // has not been read. Accepting that no longer grants ORDINARY sign-off — it routes to the
      // exception, which is what signing unread work actually is.
      if (ackedArtifacts.has(task.id)) {
        return {
          allowed: false,
          exceptional: true,
          reason: 'What the agent produced could not be loaded.',
        };
      }
      return { allowed: false, reason: 'Open what the agent produced before signing off.' };
    }
    const unread = detail.artifacts.filter(gatesSignOff).filter((a) => !readArtifacts.has(a.id));
    if (unread.length > 0) {
      return { allowed: false, reason: 'Open what the agent produced before signing off.' };
    }
    // TASK-758: an artifact that cannot be opened at all. Previously a one-click acknowledgement
    // unblocked the ✓ — and since BOTH live `email_draft` artifacts on the demo account are
    // unresolvable, that hatch was not the edge case it was designed as, it was the only route.
    // An escape hatch that is the normal path is the rubber stamp with an extra click. Ordinary
    // sign-off is blocked; the exception below demands a written reason and records it.
    const unopenable = detail.artifacts.filter(requiresAcknowledgement);
    if (unopenable.length > 0) {
      return {
        allowed: false,
        exceptional: true,
        reason: 'This work cannot be opened, so it cannot be signed off in the ordinary way.',
      };
    }
    return { allowed: true };
  };

  /**
   * TASK-922: the one door into sign-off. Work bound to a signal subject is diverted into
   * the outcome picker first; everything else goes straight through, exactly as before.
   */
  const requestSignOff = (task: Task, exception?: string) => {
    if (task.subject_key) {
      // Close the exception form as the picker opens: it carries the reason forward, and
      // showing both at once asks the same question twice.
      setExceptionFor(null);
      setOutcomeFor({ taskId: task.id, exceptionReason: exception });
      return;
    }
    void signOff(task, exception);
  };

  const signOff = async (
    task: Task,
    exceptionReason?: string,
    submission?: TaskOutcomeSubmission,
  ) => {
    try {
      await completeTask.mutateAsync(
        exceptionReason || submission
          ? { taskId: task.id, ...(exceptionReason ? { exceptionReason } : {}), ...submission }
          : task.id,
      );
      toast.success(exceptionReason ? 'Signed off with an exception' : 'Signed off');
      setExceptionFor(null);
      setExceptionReason('');
      setOutcomeFor(null);
      void query.refetch();
    } catch (err) {
      // TASK-744: the completion gate 409s with the artifacts and their review
      // states — name them, never a bare "failed".
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail as
        | { error?: string; message?: string; artifacts?: { title?: string; artifact_id?: string; state?: string }[] }
        | string
        | undefined;
      if (detail && typeof detail === 'object' && detail.error === 'completion_blocked') {
        const parts = (detail.artifacts ?? []).map(
          (a) => `${a.title || a.artifact_id}: ${(a.state ?? 'unknown').replace('_', ' ')}`,
        );
        toast.error(
          `${detail.message ?? 'Completion blocked'}${parts.length ? ` — ${parts.join(' · ')}` : ''}`,
        );
      } else {
        toast.error('Failed to sign off');
      }
    }
  };

  return (
    <WidgetContainer title={title ?? 'Waiting for your sign-off'} source="tasks">
      {query.isLoading ? (
        <div className="py-8 text-center">
          <div className="inline-block w-5 h-5 border-2 border-[#002B45] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : query.isError ? (
        // Never the empty state on a failure: "nothing is waiting" would be read as
        // "you're all clear" while drafts sit unreviewed.
        <WidgetErrorState
          title="Couldn't load what's waiting"
          detail="The task service didn't respond."
          onRetry={() => void query.refetch()}
        />
      ) : tasks.length === 0 ? (
        <p data-testid="awaiting-review-empty" className="py-6 text-center text-sm text-muted-foreground">
          Nothing is waiting for your sign-off.
        </p>
      ) : (
        <div className="space-y-2" data-testid="awaiting-review-list">
          <p className="text-xs text-muted-foreground">
            {tasks.length} {tasks.length === 1 ? 'item' : 'items'} prepared and waiting.
          </p>
          {tasks.map((task) => {
            const isOpen = expanded.has(task.id);
            const { allowed, reason, exceptional } = gate(task);
            return (
            <div
              key={task.id}
              data-testid="awaiting-review-row"
              className="rounded-md border border-border p-2"
            >
              <div className="flex items-start gap-2">
                <button
                  onClick={() => toggle(task.id)}
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? 'Hide' : 'Show'} what the agent produced for ${task.title}`}
                  className="mt-0.5 flex-shrink-0 text-muted-foreground hover:text-foreground"
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
                <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                <div className="min-w-0 flex-1">
                  <button
                    onClick={() => navigate(`/tasks?highlight=${task.id}`)}
                    className="truncate text-left text-sm font-medium text-foreground hover:underline"
                  >
                    {task.title}
                  </button>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                    <span className={`rounded px-1.5 py-0.5 ${getPriorityClass(task.priority)}`}>
                      {task.priority}
                    </span>
                    {task.updated_at && <span>prepared {formatDistanceToNow(new Date(task.updated_at))} ago</span>}
                  </div>
                </div>
                <button
                  onClick={() => requestSignOff(task)}
                  disabled={!allowed}
                  // A disabled control with no stated reason is a dead end for anyone who
                  // cannot see the amber note beneath it.
                  aria-label={
                    allowed ? `Sign off ${task.title}` : `Sign off ${task.title} — ${reason}`
                  }
                  data-testid="sign-off"
                  className="flex-shrink-0 text-gray-400 transition-colors hover:text-green-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-gray-400"
                  title={reason ?? 'Sign off'}
                >
                  <CheckCircle className="h-4 w-4" />
                </button>
              </div>
              {/* Signing off is a decision made AFTER reading the output, so the output is
                  one click away rather than on another page. */}
              {isOpen && (
                <div className="mt-2 border-t border-border pt-2 pl-6">
                  <ReviewRowDetail
                    taskId={task.id}
                    onLoaded={rememberDetail}
                    acknowledged={ackedArtifacts}
                    onOpened={(artifact) =>
                      setReadArtifacts((prev) => new Set(prev).add(artifact.id))
                    }
                    onAcknowledged={(artifact) =>
                      setAckedArtifacts((prev) => new Set(prev).add(artifact.id))
                    }
                    onUnreadable={(id) => setAckedArtifacts((prev) => new Set(prev).add(id))}
                  />
                </div>
              )}
              {!allowed && (
                <div className="mt-1 pl-6">
                  <p className="text-[11px] text-amber-800" data-testid="sign-off-blocked">
                    {reason}
                  </p>
                  {/* TASK-758: the exception is a DIFFERENT control from the ✓, on purpose. It
                      must not be reachable by the same reflex that signs readable work. */}
                  {exceptional && exceptionFor !== task.id && (
                    <button
                      type="button"
                      onClick={() => {
                        setExceptionFor(task.id);
                        setExceptionReason('');
                      }}
                      data-testid="signoff-exception-open"
                      className="mt-1 rounded border border-amber-400 px-1.5 py-0.5 text-[11px] font-medium text-amber-900 hover:bg-amber-100"
                    >
                      Sign off with an exception…
                    </button>
                  )}
                  {exceptional && exceptionFor === task.id && (
                    <div className="mt-1 space-y-1">
                      <label
                        htmlFor={`exception-${task.id}`}
                        className="block text-[11px] text-amber-900"
                      >
                        Why are you signing off work you could not read? This is recorded
                        against your name.
                      </label>
                      <textarea
                        id={`exception-${task.id}`}
                        value={exceptionReason}
                        onChange={(e) => setExceptionReason(e.target.value)}
                        rows={2}
                        data-testid="signoff-exception-reason"
                        className="w-full rounded border border-amber-300 p-1 text-[11px]"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={exceptionReason.trim().length < MIN_EXCEPTION_REASON}
                          onClick={() => requestSignOff(task, exceptionReason.trim())}
                          data-testid="signoff-exception-confirm"
                          className="rounded bg-amber-700 px-2 py-0.5 text-[11px] font-medium text-white disabled:opacity-40"
                        >
                          Record exception and sign off
                        </button>
                        <button
                          type="button"
                          onClick={() => setExceptionFor(null)}
                          className="text-[11px] text-muted-foreground hover:underline"
                        >
                          Cancel
                        </button>
                        {exceptionReason.trim().length < MIN_EXCEPTION_REASON && (
                          <span className="text-[11px] text-muted-foreground">
                            {MIN_EXCEPTION_REASON - exceptionReason.trim().length} more
                            character(s)
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* TASK-922: the same picker TaskCard uses, so the sign-off path and the Do
                  column cannot answer the server differently. */}
              {outcomeFor?.taskId === task.id && (
                <div className="mt-1 pl-6">
                  <TaskOutcomePicker
                    task={task}
                    busy={completeTask.isPending}
                    onCancel={() => setOutcomeFor(null)}
                    onConfirm={(submission) =>
                      void signOff(task, outcomeFor.exceptionReason, submission)
                    }
                  />
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </WidgetContainer>
  );
}

export default TasksAwaitingReview;
